import type {Component, Editor} from 'grapesjs';
import {isFieldEnabled, moveBoxInForm, TOGGLEABLE_BOX_TYPES, MAX_CUSTOM_FIELDS} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import {POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX, buildCustomBoxType} from '@FormMakerReact/components/postermywall-custom-box/postermywall-custom-box';
import {POSTERMYWALL_EMAIL_FORM_TYPE} from '@FormMakerReact/components/postermywall-email-form/postermywall-email-form';
import {POSTERMYWALL_POLICY_TYPE} from '@FormMakerReact/components/postermywall-policy/postermywall-policy';
import {POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE} from '@FormMakerReact/components/postermywall-submit-button-container/postermywall-submit-button-container';
import {PMW_CUSTOM_INPUT} from '@FormMakerReact/form-maker.types';
import type {FieldMeta} from '@FormMakerReact/form-maker.types';
import type {FieldRowConfig} from './field-toggle-list-control.types';
import {STANDARD_BOX_TYPES, STANDARD_CONFIG_BY_BOX_TYPE} from './field-toggle-list-control.types';

const TRAILER_TYPES: ReadonlySet<string> = new Set([POSTERMYWALL_POLICY_TYPE, POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE]);

export function isToggleableBoxType(boxType: string): boolean {
  return (TOGGLEABLE_BOX_TYPES as readonly string[]).includes(boxType);
}

export function isCustomBoxType(boxType: string): boolean {
  if (!boxType.startsWith(`${POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX}-`)) {
    return false;
  }
  const suffix = boxType.slice(POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX.length + 1);
  const idx = parseInt(suffix, 10);
  return !isNaN(idx) && idx >= 1 && idx <= MAX_CUSTOM_FIELDS;
}

export function getCustomFieldIndexFromBoxType(boxType: string): number | undefined {
  if (!isCustomBoxType(boxType)) {
    return undefined;
  }
  const suffix = boxType.slice(POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX.length + 1);
  return parseInt(suffix, 10);
}

export function buildEnabledSet(editor: Editor | null): ReadonlySet<string> {
  if (!editor) {
    return new Set<string>();
  }
  const enabled = new Set<string>();

  (TOGGLEABLE_BOX_TYPES as readonly string[]).forEach((boxType) => {
    if (isFieldEnabled(editor, boxType)) {
      enabled.add(boxType);
    }
  });

  for (let i = 1; i <= MAX_CUSTOM_FIELDS; i += 1) {
    const boxType = buildCustomBoxType(i);
    if (isFieldEnabled(editor, boxType)) {
      enabled.add(boxType);
    }
  }

  return enabled;
}

export function getCanvasBoxTypeOrder(editor: Editor | null): string[] {
  if (!editor) {
    return [];
  }
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return [];
  }
  const emailForms = wrapper.findType(POSTERMYWALL_EMAIL_FORM_TYPE);
  if (emailForms.length === 0) {
    return [];
  }
  const ordered: string[] = [];
  const formChildren: Component[] = emailForms[0].components().models;
  formChildren.forEach((child) => {
    const childType: string = child.get('type') ?? '';
    if (STANDARD_BOX_TYPES.has(childType) || isCustomBoxType(childType)) {
      ordered.push(childType);
    }
  });
  return ordered;
}

export function buildInitialRowOrder(editor: Editor | null, fieldsMeta: readonly FieldMeta[]): string[] {
  const canvasOrder = getCanvasBoxTypeOrder(editor);
  const seen = new Set<string>(canvasOrder);
  const tail: string[] = [];
  metaCustomBoxTypesInOrder(fieldsMeta).forEach((boxType) => {
    if (!seen.has(boxType)) {
      tail.push(boxType);
      seen.add(boxType);
    }
  });
  STANDARD_CONFIG_BY_BOX_TYPE.forEach((config) => {
    if (!seen.has(config.boxType)) {
      tail.push(config.boxType);
      seen.add(config.boxType);
    }
  });
  return [...canvasOrder, ...tail];
}

function metaCustomBoxTypesInOrder(fieldsMeta: readonly FieldMeta[]): string[] {
  const seen = new Set<number>();
  return [...fieldsMeta]
    .sort((a, b) => a.order - b.order)
    .reduce<string[]>((acc, entry) => {
      if (entry.component !== PMW_CUSTOM_INPUT || entry.customFieldIndex === undefined) {
        return acc;
      }
      if (seen.has(entry.customFieldIndex)) {
        return acc;
      }
      seen.add(entry.customFieldIndex);
      acc.push(buildCustomBoxType(entry.customFieldIndex));
      return acc;
    }, []);
}

export function mergeOrderFromCanvas(prev: readonly string[], canvasTypes: readonly string[], fieldsMeta: readonly FieldMeta[]): string[] {
  const known = new Set<string>(prev);
  const additions: string[] = [];
  canvasTypes.forEach((bt) => {
    if (!known.has(bt)) {
      additions.push(bt);
      known.add(bt);
    }
  });
  metaCustomBoxTypesInOrder(fieldsMeta).forEach((bt) => {
    if (!known.has(bt)) {
      additions.push(bt);
      known.add(bt);
    }
  });
  if (additions.length === 0) {
    return prev.slice();
  }
  return [...prev, ...additions];
}

export function buildCustomRowConfig(customIndex: number): FieldRowConfig {
  return {
    boxType: buildCustomBoxType(customIndex),
    labelKey: '',
    iconClass: 'icon-pencil-line',
    editKind: 'custom',
    isExpandable: true,
    isLocked: false,
    customFieldIndex: customIndex,
  };
}

export function resolveRowConfig(boxType: string): FieldRowConfig | null {
  const standard = STANDARD_CONFIG_BY_BOX_TYPE.get(boxType);
  if (standard) {
    return standard;
  }
  const customIdx = getCustomFieldIndexFromBoxType(boxType);
  if (customIdx === undefined) {
    return null;
  }
  return buildCustomRowConfig(customIdx);
}

export function resolveRowLabel(config: FieldRowConfig, fieldsMeta: FieldMeta[]): string {
  if (config.customFieldIndex === undefined) {
    return window.i18next.t(config.labelKey);
  }
  const entry = fieldsMeta.find((f) => f.component === PMW_CUSTOM_INPUT && f.customFieldIndex === config.customFieldIndex);
  const rawLabel = entry?.label ?? '';
  if (!rawLabel || rawLabel.startsWith('pmwjs_')) {
    const fallbackKey = rawLabel || '';
    return fallbackKey ? window.i18next.t(fallbackKey) : `${window.i18next.t('pmwjs_custom_field')} ${config.customFieldIndex.toString()}`;
  }
  return rawLabel;
}

export function translateRowIndexToFormChildIndex(editor: Editor | null, targetRowIndex: number): number {
  if (!editor) {
    return targetRowIndex;
  }
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return targetRowIndex;
  }
  const emailForms = wrapper.findType(POSTERMYWALL_EMAIL_FORM_TYPE);
  if (emailForms.length === 0) {
    return targetRowIndex;
  }
  const formChildren: Component[] = emailForms[0].components().models;
  let recognisedSeen = 0;
  let firstTrailerIdx = -1;
  for (let i = 0; i < formChildren.length; i += 1) {
    const childType: string = formChildren[i].get('type') ?? '';
    if (TRAILER_TYPES.has(childType)) {
      if (firstTrailerIdx === -1) {
        firstTrailerIdx = i;
      }
      continue;
    }
    if (firstTrailerIdx !== -1) {
      continue;
    }
    if (STANDARD_BOX_TYPES.has(childType) || isCustomBoxType(childType)) {
      if (recognisedSeen === targetRowIndex) {
        return i;
      }
      recognisedSeen += 1;
    }
  }
  if (firstTrailerIdx !== -1) {
    return firstTrailerIdx;
  }
  return formChildren.length;
}

export function slotBoxAtRow(editor: Editor, boxType: string, rowOrder: readonly string[]): void {
  const targetRowIndex = rowOrder.indexOf(boxType);
  if (targetRowIndex === -1) {
    return;
  }
  moveBoxInForm(editor, boxType, translateRowIndexToFormChildIndex(editor, targetRowIndex));
}

export function toggleSetMember<T>(prev: ReadonlySet<T>, value: T, add: boolean): ReadonlySet<T> {
  const next = new Set(prev);
  if (add) {
    next.add(value);
  } else {
    next.delete(value);
  }
  return next;
}

export function subscribeToCanvasMutations(editor: Editor, syncFromCanvas: () => void): () => void {
  editor.on('component:add', syncFromCanvas);
  editor.on('component:remove', syncFromCanvas);
  return (): void => {
    editor.off('component:add', syncFromCanvas);
    editor.off('component:remove', syncFromCanvas);
  };
}
